import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { hasBrushlessMotor, isCarbonBrushItem } from "@/lib/motorTypes";

export interface WorkflowItem {
  id: string;
  category: string;
  appliance_type: string;
  brand: string | null;
  title: string;
  description: string | null;
  content: Record<string, unknown> | null;
  sort_order: number | null;
  estimated_minutes?: number | null;
}

export interface WorkflowDefaults {
  errorCodes: WorkflowItem[];
  troubleshooting: WorkflowItem[];
  partsReplacement: WorkflowItem[];
  refurbishStart: WorkflowItem[];
  verification: WorkflowItem[];
  cleaning: WorkflowItem[];
  tips: WorkflowItem[];
}

// Map category slug to DB appliance_type
const categoryToApplianceType: Record<string, string> = {
  "washing-machine": "Washing Machine",
  "washing machine": "Washing Machine",
  "dryer": "Dryer",
  "tumble dryer": "Dryer",
  "washer-dryer": "Washer Dryer",
  "washer dryer": "Washer Dryer",
  "dishwasher": "Dishwasher",
  "oven": "Oven",
  "refrigerator": "Household Refrigerator",
  "household refrigerator": "Household Refrigerator",
  "vacuum-cleaner": "Vacuum Cleaner",
};

const normalizeApplianceType = (category: string): string => {
  const lower = category.toLowerCase();
  return categoryToApplianceType[lower] || category;
};

const normalizeBrand = (brand: string): string => {
  // Capitalize first letter, rest lowercase to match DB format
  const b = brand.trim();
  if (!b) return b;
  // Special cases
  const upper = b.toUpperCase();
  if (upper === "AEG") return "AEG";
  if (upper === "LG") return "LG";
  return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
};

export function useWorkflowDefaults(brand: string | null, category: string | null, workflowType?: "repair" | "refurbish", modelNumber?: string | null) {
  const [defaults, setDefaults] = useState<WorkflowDefaults>({
    errorCodes: [],
    troubleshooting: [],
    partsReplacement: [],
    refurbishStart: [],
    verification: [],
    cleaning: [],
    tips: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!brand || !category) return;

    const applianceType = normalizeApplianceType(category);
    const normalizedBrand = normalizeBrand(brand);

    const fetchDefaults = async () => {
      setIsLoading(true);
      try {
        // Fetch all workflow items for this appliance type
        const { data: items, error: itemsError } = await supabase
          .from("workflow_items")
          .select("*")
          .eq("appliance_type", applianceType)
          .order("sort_order", { ascending: true });

        if (itemsError) {
          console.error("Error fetching workflow items:", itemsError);
          setIsLoading(false);
          return;
        }

        // Fetch brand-specific defaults
        const { data: defs, error: defsError } = await supabase
          .from("workflow_defaults")
          .select("*")
          .eq("appliance_type", applianceType)
          .eq("brand", normalizedBrand);

        // Fetch parent (brand-agnostic) defaults
        const { data: parentDefs, error: parentDefsError } = await supabase
          .from("workflow_defaults")
          .select("*")
          .eq("appliance_type", applianceType)
          .is("brand", null);

        if (defsError || parentDefsError) {
          console.error("Error fetching workflow defaults:", defsError || parentDefsError);
          setIsLoading(false);
          return;
        }

        // Merge: brand-specific overrides parent. If brand-specific exists for an item, use it; otherwise fall back to parent.
        const brandMap = new Map(
          (defs || []).map((d: { workflow_item_id: string; for_repair: boolean; for_refurbish: boolean; estimated_minutes: number | null }) => [
            d.workflow_item_id,
            d,
          ])
        );
        const parentMap = new Map(
          (parentDefs || []).map((d: { workflow_item_id: string; for_repair: boolean; for_refurbish: boolean; estimated_minutes: number | null }) => [
            d.workflow_item_id,
            d,
          ])
        );

        // Merged map: brand overrides parent
        const defaultMap = new Map<string, { for_repair: boolean; for_refurbish: boolean }>();
        for (const [id, val] of parentMap) {
          defaultMap.set(id, val);
        }
        for (const [id, val] of brandMap) {
          defaultMap.set(id, val);
        }

        // Resolved estimated_minutes per item: brand override > parent default > item base
        const minutesMap = new Map<string, number | null>();
        for (const [itemId, val] of parentMap) {
          if ((val as any).estimated_minutes != null) minutesMap.set(itemId, Number((val as any).estimated_minutes));
        }
        for (const [itemId, val] of brandMap) {
          if ((val as any).estimated_minutes != null) minutesMap.set(itemId, Number((val as any).estimated_minutes));
        }

        const isBrushless = hasBrushlessMotor(normalizedBrand, modelNumber);

        // Filter items: if workflowType specified, only include items that are checked for that type
        const filterByType = (item: WorkflowItem): boolean => {
          // Hide carbon-brush steps for brushless / inverter motors
          if (isBrushless && isCarbonBrushItem(item.title)) return false;
          const def = defaultMap.get(item.id) as { for_repair: boolean; for_refurbish: boolean } | undefined;
          if (!def) return false;
          if (workflowType === "repair") return def.for_repair;
          if (workflowType === "refurbish") return def.for_refurbish;
          return def.for_repair || def.for_refurbish;
        };

        const allItems = ((items || []) as WorkflowItem[]).map((it) => {
          const overridden = minutesMap.get(it.id);
          return overridden != null
            ? { ...it, estimated_minutes: overridden }
            : it;
        });

        // Error codes are brand-specific
        const errorCodes = allItems
          .filter((i) => i.category === "error_codes" && i.brand === normalizedBrand)
          .filter(filterByType);

        // Other categories: include both brand-agnostic AND brand-specific items
        const troubleshooting = allItems
          .filter((i) => i.category === "troubleshooting" && (!i.brand || i.brand === normalizedBrand))
          .filter(filterByType);

        const partsReplacement = allItems
          .filter((i) => i.category === "parts_replacement" && (!i.brand || i.brand === normalizedBrand))
          .filter(filterByType);

        const refurbishStart = allItems
          .filter((i) => i.category === "refurbish_start" && (!i.brand || i.brand === normalizedBrand))
          .filter(filterByType);

        const verification = allItems
          .filter((i) => i.category === "verification" && (!i.brand || i.brand === normalizedBrand))
          .filter(filterByType);

        const cleaning = allItems
          .filter((i) => i.category === "cleaning" && (!i.brand || i.brand === normalizedBrand))
          .filter(filterByType);

        // Tips can be brand-agnostic OR brand-specific
        const tips = allItems
          .filter((i) => i.category === "tips" && (!i.brand || i.brand === normalizedBrand))
          .filter(filterByType);

        setDefaults({ errorCodes, troubleshooting, partsReplacement, refurbishStart, verification, cleaning, tips });
      } catch (err) {
        console.error("Error in useWorkflowDefaults:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefaults();
  }, [brand, category, workflowType, modelNumber]);

  return { defaults, isLoading };
}

/**
 * Get all error codes for a brand/category (no workflow type filter - just all error codes)
 */
export function useErrorCodes(brand: string | null, category: string | null) {
  const [errorCodes, setErrorCodes] = useState<WorkflowItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!brand || !category) return;

    const applianceType = normalizeApplianceType(category);
    const normalizedBrand = normalizeBrand(brand);

    const fetch = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("workflow_items")
          .select("*")
          .eq("appliance_type", applianceType)
          .eq("category", "error_codes")
          .eq("brand", normalizedBrand)
          .order("sort_order", { ascending: true });

        if (!error && data) {
          setErrorCodes(data as WorkflowItem[]);
        }
      } catch (err) {
        console.error("Error fetching error codes:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, [brand, category]);

  return { errorCodes, isLoading };
}
